import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Search } from 'lucide-react'
import { sortedCountryCodes } from '@/lib/data/country-codes'
import { useState } from 'react'
import type { ContactFormData } from './schema'

interface ContactFormProps {
    form: UseFormReturn<ContactFormData>
    isEditing?: boolean
}

export function ContactForm({ form, isEditing = false }: ContactFormProps) {
    const { t } = useTranslation()
    const [phonePrefixSearch, setPhonePrefixSearch] = useState('')

    return (
        <div className="space-y-4">
            <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{t('contacts.firstName')} <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                            <Input
                                placeholder={t('contacts.firstNamePlaceholder')}
                                {...field}
                                value={field.value || ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="surname"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{t('contacts.surname')}</FormLabel>
                        <FormControl>
                            <Input
                                placeholder={t('contacts.surnamePlaceholder')}
                                {...field}
                                value={field.value || ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{t('contacts.email')}</FormLabel>
                        <FormControl>
                            <Input
                                type="email"
                                placeholder={t('contacts.emailPlaceholder')}
                                {...field}
                                value={field.value || ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <div className="space-y-2">
                <Label>{t('contacts.phonePrefix')}</Label>
                <FormField
                    control={form.control}
                    name="phonePrefix"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <Select
                                    value={field.value || undefined}
                                    onValueChange={(value) => {
                                        field.onChange(value || '')
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('contacts.phonePrefixPlaceholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <div className="px-2 py-1.5 border-b">
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                <Input
                                                    placeholder="Search countries..."
                                                    value={phonePrefixSearch}
                                                    onChange={(e) => setPhonePrefixSearch(e.target.value)}
                                                    className="pl-8 h-8"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                        {sortedCountryCodes
                                            .filter((country) => {
                                                if (!phonePrefixSearch) return true
                                                const searchLower = phonePrefixSearch.toLowerCase()
                                                const countryName = country.country.toLowerCase()
                                                const dialCode = country.dialCode.toLowerCase()
                                                return countryName.includes(searchLower) || dialCode.includes(searchLower)
                                            })
                                            .map((country) => (
                                                <SelectItem key={country.code} value={country.dialCode}>
                                                    {country.country} ({country.dialCode})
                                                </SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{t('contacts.phone')}</FormLabel>
                        <FormControl>
                            <Input
                                type="tel"
                                placeholder={t('contacts.phonePlaceholder')}
                                {...field}
                                value={field.value || ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{t('contacts.position')}</FormLabel>
                        <FormControl>
                            <Input
                                placeholder={t('contacts.positionPlaceholder')}
                                {...field}
                                value={field.value || ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="team"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{t('contacts.team')}</FormLabel>
                        <FormControl>
                            <Input
                                placeholder={t('contacts.teamPlaceholder')}
                                {...field}
                                value={field.value || ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </div>
    )
}
